import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';

interface LoadingSkeletonProps {
  variant?: 'lobby' | 'table' | 'profile';
}

export function LoadingSkeleton({ variant = 'lobby' }: LoadingSkeletonProps): JSX.Element {
  if (variant === 'table') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-900 via-green-800 to-green-900 p-6">
        <div className="max-w-6xl mx-auto">
          <Skeleton className="h-12 w-64 mb-6 bg-white/10" />
          
          <Card className="bg-black/40 border-yellow-600/30">
            <CardContent className="p-6">
              <div className="aspect-video bg-green-900/50 rounded-lg relative mb-6">
                <Skeleton className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 h-32 w-64 bg-white/10" />
              </div>
              
              <div className="flex justify-around mb-4">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <Skeleton key={i} className="h-20 w-16 rounded-lg bg-white/10" />
                ))}
              </div>
              
              <div className="flex justify-center space-x-4">
                <Skeleton className="h-12 w-32 bg-white/10" />
                <Skeleton className="h-12 w-32 bg-white/10" />
                <Skeleton className="h-12 w-32 bg-white/10" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (variant === 'profile') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-900 via-green-800 to-green-900 p-6">
        <div className="max-w-4xl mx-auto">
          <Skeleton className="h-12 w-48 mb-6 bg-white/10" />
          
          <Card className="bg-black/40 border-yellow-600/30 mb-6">
            <CardContent className="p-6">
              <div className="flex items-center space-x-4 mb-6">
                <Skeleton className="h-20 w-20 rounded-full bg-white/10" />
                <div className="flex-1">
                  <Skeleton className="h-6 w-48 mb-2 bg-white/10" />
                  <Skeleton className="h-4 w-64 bg-white/10" />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-24 bg-white/10 rounded-lg" />
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-900 via-green-800 to-green-900 p-6">
      <div className="max-w-7xl mx-auto">
        <Skeleton className="h-12 w-64 mb-6 bg-white/10" />
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="bg-black/30 border-green-500/30">
              <CardContent className="p-4">
                <Skeleton className="h-8 w-8 mx-auto mb-2 bg-white/10" />
                <Skeleton className="h-8 w-24 mx-auto mb-2 bg-white/10" />
                <Skeleton className="h-4 w-32 mx-auto bg-white/10" />
              </CardContent>
            </Card>
          ))}
        </div>
        
        <div className="max-w-2xl mx-auto">
          <Card className="bg-black/40 border-yellow-600/30">
            <Skeleton className="h-32 w-full bg-white/10 mb-6" />
            <CardContent className="p-6">
              <div className="space-y-4">
                <Skeleton className="h-4 w-full bg-white/10" />
                <Skeleton className="h-4 w-full bg-white/10" />
                <Skeleton className="h-4 w-3/4 bg-white/10" />
                <Skeleton className="h-12 w-full bg-white/10" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
